/**
 * Shared helper for auto-recovering from stale dynamic-import ("chunk load")
 * failures after a redeploy changes Vite's asset hashes.
 *
 * Root cause: the app lazy-loads ~180 routes via React.lazy(). After a new
 * deploy, a browser tab that was already open still references the OLD
 * chunk filenames; navigating to a route that needs a fresh lazy import
 * 404s, and the user is stuck on a dead-end "Something went wrong" screen.
 *
 * Two independent call sites use this helper as defense-in-depth:
 *
 *  1. main.tsx's `vite:preloadError` listener — Vite's own preload helper
 *     (which wraps every lazy()/dynamic import in the build output)
 *     dispatches this event for exactly this failure, before the rejection
 *     would otherwise reach React. This is the earliest, most reliable hook.
 *  2. components/common/ErrorBoundary.tsx — the boundary that actually wraps
 *     every workspace's routed <Outlet/>, so it's the one that catches a
 *     failed lazy() import as a React render error. This is a backstop for
 *     anything the first listener doesn't cover (e.g. the loop-guard window
 *     from case 1 already elapsed, or a chunk failure that reaches React by
 *     some other path). Plain `window.onerror` / `unhandledrejection`
 *     listeners do NOT see this error: React.lazy() catches the rejected
 *     import() promise internally and re-throws it as a render error, so it
 *     never becomes an "unhandled" browser event.
 *
 * Loop guard: reload only if no chunk-reload has happened in the last
 * RELOAD_WINDOW_MS. A genuine stale chunk self-heals on the first reload
 * (fresh index.html -> fresh hashes -> the route loads). A truly-broken
 * chunk (bad deploy, network outage) would fail again immediately, so we
 * give up after one attempt and let the caller fall back to a manual
 * "try again" UI instead of reload-looping forever. The window re-arms
 * itself automatically once it elapses — no manual clear needed — so a
 * LATER deploy's stale chunk still gets its own auto-recovery attempt.
 */

const RELOAD_TIMESTAMP_KEY = "yudu_chunk_reload_at";
const RELOAD_WINDOW_MS = 10_000;

const CHUNK_ERROR_PATTERNS: readonly RegExp[] = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
  /ChunkLoadError/,
  /Loading chunk .* failed/i,
];

function extractNameAndMessage(error: unknown): { name: string; message: string } {
  if (error && typeof error === "object") {
    const withFields = error as { name?: unknown; message?: unknown };
    return {
      name: "name" in error ? String(withFields.name ?? "") : "",
      message: "message" in error ? String(withFields.message ?? "") : "",
    };
  }
  return { name: "", message: String(error ?? "") };
}

/** True if `error` looks like a stale/failed dynamic-import chunk load. */
export function isChunkLoadError(error: unknown): boolean {
  if (error === null || error === undefined) return false;
  const { name, message } = extractNameAndMessage(error);
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(name) || pattern.test(message));
}

/**
 * Reload the page — but only if no chunk-reload has happened in the last
 * ~10s (see module doc for why). Returns true if a reload was triggered
 * (the page is navigating away; callers should stop doing anything else),
 * or false if the guard blocked it (callers should fall through to a
 * manual fallback UI).
 */
export function attemptChunkReload(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_TIMESTAMP_KEY) || 0);
    if (Date.now() - last <= RELOAD_WINDOW_MS) {
      return false;
    }
    // Record the attempt BEFORE reloading. If this throws (e.g. Safari
    // private-mode setItem), we fall to the catch below and skip the
    // reload entirely rather than risk a loop we have no way to guard.
    window.sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}
